import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

// Expiração da sessão: 24h a partir do login.
//
// NOTA DE HONESTIDADE: o ideal seria o `sessions_timebox` do Supabase, que expira
// no SERVIDOR e não pode ser burlado. Ele é exclusivo do plano Pro. Aqui a
// checagem é do lado do cliente: guardamos o instante do login e deslogamos ao
// passar de 24h. Protege o uso normal (esquecer aberto no celular), mas não é
// barreira contra alguém que extraia o refresh token do navegador.
const MAX_SESSION_MS = 24 * 60 * 60 * 1000
const LOGIN_AT_KEY = 'easymoney.login-at'
const CHECK_INTERVAL_MS = 60 * 1000

interface AuthContextValue {
  session: Session | null
  email: string | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  /** Instante (ms) em que a sessão atual expira. null se não há sessão. */
  expiresAt: number | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

function loginAt(): number | null {
  const v = localStorage.getItem(LOGIN_AT_KEY)
  const n = v ? Number(v) : NaN
  return Number.isFinite(n) ? n : null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  async function forceSignOut() {
    localStorage.removeItem(LOGIN_AT_KEY)
    await supabase.auth.signOut()
    setSession(null)
  }

  // Sessão expirada pelas nossas 24h? (independe do refresh do Supabase)
  function isStale(): boolean {
    const t = loginAt()
    return t == null || Date.now() - t > MAX_SESSION_MS
  }

  useEffect(() => {
    let alive = true

    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return
      // Sessão restaurada do storage: só vale se ainda estiver dentro das 24h.
      if (data.session && isStale()) {
        void forceSignOut()
        setLoading(false)
        return
      }
      setSession(data.session)
      setLoading(false)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (!alive) return
      if (event === 'SIGNED_IN') localStorage.setItem(LOGIN_AT_KEY, String(Date.now()))
      if (event === 'SIGNED_OUT') localStorage.removeItem(LOGIN_AT_KEY)
      setSession(s)
    })

    // Vigia as 24h mesmo com a aba aberta o tempo todo.
    const timer = setInterval(() => {
      if (session && isStale()) void forceSignOut()
    }, CHECK_INTERVAL_MS)

    return () => {
      alive = false
      sub.subscription.unsubscribe()
      clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    localStorage.setItem(LOGIN_AT_KEY, String(Date.now()))
  }

  const t = loginAt()
  const value: AuthContextValue = {
    session,
    email: session?.user?.email ?? null,
    loading,
    signIn,
    signOut: forceSignOut,
    expiresAt: session && t != null ? t + MAX_SESSION_MS : null,
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return ctx
}
